import type { useI18n } from 'vue-i18n';
import type { MonospaceSpace, ResolvedMonospacePage, ResolvedMonospaceSpace, WorkspaceWrappedItem } from '#layers/data-model/public';
import type { SpaceMeta } from '#layers/spaces/types/space';
import { toValue } from 'vue';
import { toSortedArray } from '#layers/base/public';
import { translateString } from '#layers/i18n/public';

export function translateSpace<T extends SpaceMeta | MonospaceSpace>(space: T, i18n: ReturnType<typeof useI18n>): T {
	return {
		...space,
		title: translateString(toValue(space.title), i18n),
		...('sections' in space && Array.isArray(space.sections)
			? { sections: space.sections.map((section) => ({ ...section, title: translateString(toValue(section.title), i18n) })) }
			: {}),
	};
}

export function translatePage<T extends { title: string }>(page: T, i18n: ReturnType<typeof useI18n>): T {
	return {
		...page,
		title: translateString(toValue(page.title), i18n),
	};
}

export function getSpaceNextSortValue(space: ResolvedMonospaceSpace): number {
	return Math.max(-1, ...space.pages.filter((p) => !p.sectionId).map((p) => p.sort), ...space.sections.map((s) => s.sort)) + 1;
}

export function getSpaceFirstPage<T extends ResolvedMonospaceSpace | WorkspaceWrappedItem<'MonospaceSpace'>>(space: T): (T extends WorkspaceWrappedItem<'MonospaceSpace'> ? WorkspaceWrappedItem<'MonospacePage'> : ResolvedMonospacePage) | null {
	const sortedPagesAndSections = toSortedArray([...space.pages, ...(space.sections ?? [])]);

	for (const item of sortedPagesAndSections) {
		if (item.$collection === 'MonospacePageSection') {
			const pages = item.pages;

			if (pages.length > 0) {
				return toSortedArray(pages)[0]!;
			}
		}
		else {
			return item;
		}
	}

	return null;
}
